import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { Plus, Trash2, Edit2, ArrowUp, ArrowDown, BookOpen, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { BibtexImportModal } from "./BibtexImportModal";
import {
  publicationsContentSchema,
  type PublicationsContent,
  type PublicationItem,
  type PublicationType,
} from "@profolio/validators";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";

interface PublicationsEditorProps {
  content: Record<string, unknown>;
  onChange: (content: PublicationsContent) => void;
  userTier?: string;
  onUpgradeClick?: () => void;
}

const emptyItem: PublicationItem = {
  title: "",
  authors: "",
  year: new Date().getFullYear(),
  venue: "",
  type: "journal",
  doi: "",
  url: "",
  pdfUrl: "",
  codeUrl: "",
  abstract: "",
  featured: false,
};

export function PublicationsEditor({
  content,
  onChange,
  userTier = "starter",
  onUpgradeClick,
}: PublicationsEditorProps) {
  const productTier = mapSubscriptionToProductTier(userTier);
  const isProUser = productTier === "pro" || productTier === "premium";

  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<PublicationItem>(emptyItem);
  const [formError, setFormError] = useState<string | null>(null);
  const [modalDetailsOpen, setModalDetailsOpen] = useState(false);

  const handleImportPublication = (importedData: Partial<PublicationItem>) => {
    setFormData({
      ...emptyItem,
      ...importedData,
      year: importedData.year || new Date().getFullYear(),
    });
    setEditingIndex(null);
    setFormError(null);
    setModalOpen(true);
  };

  const parseResult = publicationsContentSchema.safeParse(content);
  const data: PublicationsContent = parseResult.success
    ? parseResult.data
    : { heading: "Publications", displayStyle: "grouped_by_year", items: [] };

  const heading = data.heading || "Publications";
  const displayStyle = data.displayStyle || "grouped_by_year";
  const items: PublicationItem[] = data.items || [];

  const notifyChange = (
    newHeading: string,
    newStyle: "grouped_by_year" | "grouped_by_type" | "list",
    newItems: PublicationItem[]
  ) => {
    onChange({
      heading: newHeading,
      displayStyle: newStyle,
      items: newItems,
    });
  };

  const openAddModal = () => {
    setEditingIndex(null);
    setFormData({ ...emptyItem, year: new Date().getFullYear() });
    setFormError(null);
    setModalDetailsOpen(false);
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setFormData({ ...items[index] });
    setFormError(null);
    setModalDetailsOpen(true);
    setModalOpen(true);
  };

  const handleSaveItem = () => {
    if (!formData.title.trim()) {
      setFormError("Title is required.");
      return;
    }
    if (!formData.authors.trim()) {
      setFormError("Authors list is required.");
      return;
    }

    let updatedItems: PublicationItem[];
    if (editingIndex !== null) {
      updatedItems = [...items];
      updatedItems[editingIndex] = formData;
    } else {
      updatedItems = [formData, ...items];
    }

    notifyChange(heading, displayStyle, updatedItems);
    setModalOpen(false);
  };

  const handleDeleteItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    notifyChange(heading, displayStyle, updated);
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const updated = [...items];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    notifyChange(heading, displayStyle, updated);
  };

  return (
    <div className="space-y-6">
      {/* ── Heading ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="pub-heading" className="text-xs font-semibold">
            Section Heading
          </Label>
          <Input
            id="pub-heading"
            value={heading}
            onChange={(e) => notifyChange(e.target.value, displayStyle, items)}
            placeholder="Publications"
            className="mt-1 text-xs"
          />
        </div>

        <div>
          <Label htmlFor="pub-style" className="text-xs font-semibold">
            Organize publications by
          </Label>
          <select
            id="pub-style"
            value={displayStyle}
            onChange={(e) =>
              notifyChange(
                heading,
                e.target.value as "grouped_by_year" | "grouped_by_type" | "list",
                items
              )
            }
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card text-foreground px-3 py-1 text-xs shadow-sm transition-colors"
          >
            <option value="grouped_by_year" className="bg-card text-foreground">Year</option>
            <option value="grouped_by_type" className="bg-card text-foreground">Type</option>
            <option value="list" className="bg-card text-foreground">One list</option>
          </select>
        </div>
      </div>

      {/* ── Actions Bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
        <div>
          <h3 className="text-xs font-semibold">Publications & Papers ({items.length})</h3>
          <p className="text-[11px] text-muted-foreground">
            Add papers manually or import directly from BibTeX / DOI.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (!isProUser && onUpgradeClick) {
                onUpgradeClick();
              } else {
                setImportModalOpen(true);
              }
            }}
            className="gap-1.5 text-xs h-8"
          >
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            Import BibTeX
            {!isProUser && (
              <span className="text-[9px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 px-1 py-0.2 rounded-xs">
                PRO
              </span>
            )}
          </Button>

          <Button
            type="button"
            size="sm"
            onClick={openAddModal}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Publication
          </Button>
        </div>
      </div>

      {/* ── Publications List ── */}
      {items.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No publications added yet"
          description="Showcase your research papers, conference proceedings, or import your entire BibTeX bibliography."
          actionLabel="Add First Paper"
          onAction={openAddModal}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (!isProUser && onUpgradeClick) {
                onUpgradeClick();
              } else {
                setImportModalOpen(true);
              }
            }}
            className="gap-1.5 text-xs h-8"
          >
            <Sparkles className="h-3.5 w-3.5 text-amber-500" />
            <span>Import BibTeX</span>
          </Button>
        </EmptyState>
      ) : (
        <div className="space-y-2.5">
          {items.map((item, index) => (
            <div
              key={item.id || index}
              className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3.5 shadow-2xs hover:border-primary/30 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-xs text-foreground line-clamp-1">
                    {item.title}
                  </span>
                  {item.featured && <Badge variant="secondary" className="text-[10px]">Featured</Badge>}
                  <Badge variant="outline" className="text-[10px]">{item.year}</Badge>
                  <Badge variant="outline" className="text-[10px] capitalize">
                    {item.type?.replace("_", " ")}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground line-clamp-1">{item.authors}</p>
                {item.venue && (
                  <p className="text-[11px] italic text-muted-foreground mt-0.5 line-clamp-1">
                    {item.venue}
                  </p>
                )}
                <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                  {item.doi && (
                    <span className="text-[10px] font-mono text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded">
                      DOI: {item.doi}
                    </span>
                  )}
                  {item.url && (
                    <span className="text-[10px] text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded">
                      Publisher ↗
                    </span>
                  )}
                  {item.pdfUrl && (
                    <span className="text-[10px] text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded">
                      PDF ↓
                    </span>
                  )}
                  {item.codeUrl && (
                    <span className="text-[10px] text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded">
                      Code ↗
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={index === 0}
                  onClick={() => moveItem(index, "up")}
                  className="h-7 w-7 text-muted-foreground"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={index === items.length - 1}
                  onClick={() => moveItem(index, "down")}
                  className="h-7 w-7 text-muted-foreground"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => openEditModal(index)}
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteItem(index)}
                  className="h-7 w-7 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add / Edit Publication Dialog with Progressive Disclosure ── */}
      <Dialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editingIndex !== null ? "Edit Publication" : "Add Publication"}
        description="Enter the essential paper metadata below."
      >
        <div className="space-y-4 pt-2">
          {formError && (
            <div className="rounded-md bg-destructive/15 p-2.5 text-xs text-destructive font-medium">
              {formError}
            </div>
          )}

          {/* 1. Essential Fields */}
          <div>
            <Label htmlFor="pub-title" className="text-xs font-semibold">
              Paper / Publication Title *
            </Label>
            <Input
              id="pub-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Distributed Consensus in Cloud-Edge Environments"
              className="mt-1 text-xs"
            />
          </div>

          <div>
            <Label htmlFor="pub-authors" className="text-xs font-semibold">
              Authors List *
            </Label>
            <Input
              id="pub-authors"
              value={formData.authors}
              onChange={(e) => setFormData({ ...formData, authors: e.target.value })}
              placeholder="e.g. Jane Doe, John Smith, Alice Brown"
              className="mt-1 text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pub-year" className="text-xs font-semibold">
                Publication Year *
              </Label>
              <Input
                id="pub-year"
                type="number"
                value={formData.year}
                onChange={(e) =>
                  setFormData({ ...formData, year: parseInt(e.target.value) || 0 })
                }
                min={1900}
                max={2100}
                className="mt-1 text-xs"
              />
            </div>

            <div>
              <Label htmlFor="pub-type" className="text-xs font-semibold">
                Publication Type
              </Label>
              <Select
                id="pub-type"
                value={formData.type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type: e.target.value as PublicationType,
                  })
                }
                className="mt-1 text-xs"
              >
                <option value="journal">Journal Article</option>
                <option value="conference">Conference Paper</option>
                <option value="book_chapter">Book Chapter</option>
                <option value="thesis">Thesis / Dissertation</option>
                <option value="preprint">Preprint</option>
                <option value="other">Other</option>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="pub-venue" className="text-xs font-semibold">
              Venue / Journal / Conference Name
            </Label>
            <Input
              id="pub-venue"
              value={formData.venue || ""}
              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
              placeholder="e.g. IEEE Transactions on Software Engineering"
              className="mt-1 text-xs"
            />
          </div>

          {/* 2. Progressive Disclosure: Additional Details & Links */}
          <div className="rounded-lg border bg-muted/20 overflow-hidden">
            <button
              type="button"
              onClick={() => setModalDetailsOpen(!modalDetailsOpen)}
              className="flex w-full items-center justify-between p-3 text-xs font-medium hover:bg-muted/40 transition-colors"
            >
              <span>Additional Links, DOI & Abstract</span>
              {modalDetailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {modalDetailsOpen && (
              <div className="p-3.5 pt-1 border-t space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pub-doi" className="text-xs font-medium">
                      DOI Identifier
                    </Label>
                    <Input
                      id="pub-doi"
                      value={formData.doi || ""}
                      onChange={(e) => setFormData({ ...formData, doi: e.target.value })}
                      placeholder="10.1109/TSE.2024.123"
                      className="mt-1 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pub-url" className="text-xs font-medium">
                      Publisher / Web URL
                    </Label>
                    <Input
                      id="pub-url"
                      value={formData.url || ""}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://doi.org/..."
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="pub-pdf" className="text-xs font-medium">
                      PDF Download Link
                    </Label>
                    <Input
                      id="pub-pdf"
                      value={formData.pdfUrl || ""}
                      onChange={(e) => setFormData({ ...formData, pdfUrl: e.target.value })}
                      placeholder="https://example.com/paper.pdf"
                      className="mt-1 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pub-code" className="text-xs font-medium">
                      Code / Dataset URL
                    </Label>
                    <Input
                      id="pub-code"
                      value={formData.codeUrl || ""}
                      onChange={(e) => setFormData({ ...formData, codeUrl: e.target.value })}
                      placeholder="https://github.com/..."
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="pub-abstract" className="text-xs font-medium">
                    Abstract / Summary
                  </Label>
                  <Textarea
                    id="pub-abstract"
                    value={formData.abstract || ""}
                    onChange={(e) => setFormData({ ...formData, abstract: e.target.value })}
                    placeholder="Key findings and contributions..."
                    rows={3}
                    className="mt-1 text-xs"
                  />
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    id="pub-featured"
                    checked={formData.featured || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                  />
                  <Label htmlFor="pub-featured" className="text-xs cursor-pointer">
                    Highlight as a Featured Publication
                  </Label>
                </div>
              </div>
            )}
          </div>

          {/* 3. Modal Actions */}
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              className="text-xs h-8"
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSaveItem} className="text-xs h-8 font-medium">
              {editingIndex !== null ? "Save Changes" : "Add Paper"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* BibTeX Import Modal */}
      <BibtexImportModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        onImport={handleImportPublication}
      />
    </div>
  );
}
